import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { has } from "lodash";

import { BandInvitationService } from "Bands/services";
import {
  BAND_ACTIONS,
  IBandInvitationProps,
  InvitationStatus,
} from "Bands/types";

import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";
import { IStoreState } from "App/types";

export const createBandInvitation = createAsyncThunk(
  BAND_ACTIONS.createBandInvitation,
  async (invitation: IBandInvitationProps, { rejectWithValue }) => {
    try {
      const data = await BandInvitationService.createBandInvitation(invitation);
      const id = Object.values(data?.bandInvitation)?.[0]?.attributes?.bandUser
        ?.data.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendBandInvitation = createAsyncThunk(
  BAND_ACTIONS.sendBandInvitation,
  async (invitationId: string, { rejectWithValue }) => {
    try {
      await BandInvitationService.sendBandInvitation(invitationId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const acceptBandInvitation = createAsyncThunk(
  BAND_ACTIONS.acceptBandInvitation,
  async (invitationId: string, { rejectWithValue }) => {
    try {
      await BandInvitationService.acceptBandInvitation(invitationId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editBandInvitation = createAsyncThunk(
  BAND_ACTIONS.editBandInvitation,
  async (
    props: { id: string; data: Partial<IBandInvitationProps> },
    { rejectWithValue }
  ) => {
    try {
      const data = await BandInvitationService.editBandInvitation(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteBandInvitation = createAsyncThunk(
  BAND_ACTIONS.deleteBandInvitation,
  async (id: string, { rejectWithValue }) => {
    try {
      await BandInvitationService.deleteBandInvitation(id);
      return {
        path: API_ENDPOINTS.BAND_INVITATION(id),
        dataKey: IDataKeys.bandInvitation,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetBandInvitationAction = (
  dispatch: Dispatch<any>,
  invitationId: string
) => {
  const action = API_ENDPOINTS.BAND_INVITATION(invitationId);
  return dispatch(getBandInvitation({ invitationId, action }));
};

export const getBandInvitation = createAsyncThunk(
  BAND_ACTIONS.getBandInvitation,
  async (
    { invitationId }: { invitationId: string; action: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await BandInvitationService.getBandInvitation(invitationId);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  // Do not fetch the band invitaiton if it is already in the state.
  {
    condition: ({ invitationId }, { getState }) => {
      const state = getState() as IStoreState;
      const { bandInvitation } = state.bandData.data;
      const path = API_ENDPOINTS.BAND_INVITATION(invitationId);

      return !has(bandInvitation, path);
    },
  }
);

export const revokeBandInvitation = createAsyncThunk(
  BAND_ACTIONS.revokeBandInvitation,
  async (id: string, { rejectWithValue }) => {
    try {
      return await BandInvitationService.revokeBandInvitation(id);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetPendingBandInvitationsAction = (
  dispatch: Dispatch<any>,
  props: {
    band: string;
    page?: number;
    itemsPerPage?: number;
  } = { band: "" }
) => {
  props.page = props.page || 1;
  props.itemsPerPage = props.itemsPerPage || 15;

  return dispatch(
    getPendingBandInvitations({
      ...props,
      paginationKey: BAND_ACTIONS.getPendingBandInvitations,
      band: API_ENDPOINTS.BAND(props.band),
    })
  );
};

export const getPendingBandInvitations = createAsyncThunk(
  BAND_ACTIONS.getPendingBandInvitations,
  async (
    {
      paginationKey,
      ...props
    }: {
      band: string;
      page?: number;
      paginationKey: string;
      itemsPerPage?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await BandInvitationService.getBandInvitations({
        ...props,
        status: InvitationStatus.invitation_not_sent,
        "sort[firstname,lastname]": "asc",
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.pendingBandInvitation]: data.bandInvitation,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetSentBandInvitationsAction = (
  dispatch: Dispatch<any>,
  props: {
    band: string;
    page?: number;
    itemsPerPage?: number;
  } = { band: "" }
) => {
  props.page = props.page || 1;
  props.itemsPerPage = props.itemsPerPage || 15;

  return dispatch(
    getSentBandInvitations({
      ...props,
      paginationKey: BAND_ACTIONS.getSentBandInvitations,
      band: API_ENDPOINTS.BAND(props.band),
    })
  );
};
export const getSentBandInvitations = createAsyncThunk(
  BAND_ACTIONS.getSentBandInvitations,
  async (
    {
      paginationKey,
      ...props
    }: {
      band: string;
      page?: number;
      paginationKey: string;
      itemsPerPage?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await BandInvitationService.getBandInvitations({
        ...props,
        status: InvitationStatus.invitation_sent,
        "sort[firstname,lastname]": "asc",
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.sentBandInvitation]: data.bandInvitation,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
